import React, { useState } from 'react';
import Button from '../../ui/Button/Button';
import Dialog from '../../ui/Dialog/Dialog';
import { Field, Input, Select, Checkbox } from '../../ui/Field/Field';
import { withVat } from '../../constants/finance';
import './ServiceModal.css';

const FORM_ID = 'fsh-inventory-form';

export default function InventoryModal({ onClose, onSave, editData, isSaving, itemUsageMap = {} }) {
    const consumedQty = editData ? (itemUsageMap[editData.id] || 0) : 0;
    
    // Track the displayed "Inward Quantity" in local state to avoid math glitches while typing
    const [inwardQty, setInwardQty] = useState(editData ? (Number(editData.quantity) + consumedQty) : 0);
    
    const [formData, setFormData] = useState(editData || {
        name: '',
        partNumber: '',
        unit: 'Liter',
        quantity: 0,
        minStock: 0,
        unitCost: 0,
        totalInvoice: '',
        useTotalCalculation: false,
        isVATInclusive: true,
        vendor: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0]
    });

    const calculatePricing = (state, currentInwardQty) => {
        if (state.useTotalCalculation) {
            let total = parseFloat(state.totalInvoice) || 0;
            const qty = parseFloat(currentInwardQty) || 0;
            
            if (!state.isVATInclusive) {
                total = withVat(total); // Add 5% VAT if not included
            }
            
            if (qty > 0) {
                return (total / qty).toFixed(2);
            }
        }
        return state.unitCost;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        
        setFormData(prev => {
            const newState = { ...prev, [name]: val };
            
            // Auto-calculate unit price if in total mode
            if (newState.useTotalCalculation && (name === 'totalInvoice' || name === 'useTotalCalculation' || name === 'isVATInclusive')) {
                newState.unitCost = calculatePricing(newState, inwardQty);
            }
            
            return newState;
        });
    };

    const handleInwardQtyChange = (e) => {
        const val = e.target.value; // Keep as string while typing to allow decimal point etc.
        setInwardQty(val);
        
        const numericVal = parseFloat(val) || 0;
        setFormData(prev => {
            const newState = { 
                ...prev, 
                quantity: numericVal - consumedQty // Update the actual balance
            };
            
            // TRIGGER pricing update when quantity changes
            if (newState.useTotalCalculation) {
                newState.unitCost = calculatePricing(newState, numericVal);
            }
            
            return newState;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        saveItem(false);
    };

    const handleSaveAndNext = async (e) => {
        e.preventDefault();
        const success = await saveItem(true);
        if (success) {
            setInwardQty(0);
            setFormData(prev => ({
                ...prev,
                name: '',
                partNumber: '',
                quantity: 0,
                unitCost: 0,
                totalInvoice: '',
                useTotalCalculation: false
            }));
        }
    };

    const saveItem = async (stayOpen) => {
        const result = await onSave({
            ...formData,
            quantity: Number(formData.quantity) || 0,
            minStock: Number(formData.minStock) || 0,
            unitCost: Number(formData.unitCost) || 0
        }, stayOpen);
        return result?.success;
    };

    return (
        <Dialog
            open
            onClose={isSaving ? undefined : onClose}
            title={editData ? 'Edit Item' : 'Add New Item'}
            size="lg"
            closeOnOverlay={false}
            footer={(
                <>
                    <Button type="button" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    {!editData && (
                        <Button
                            type="button"
                            onClick={handleSaveAndNext}
                            isLoading={isSaving}
                        >
                            Save & Add Next Item
                        </Button>
                    )}
                    <Button type="submit" form={FORM_ID} variant="primary" isLoading={isSaving}>
                        {editData ? 'Update' : 'Save & Close'}
                    </Button>
                </>
            )}
        >
            <form id={FORM_ID} onSubmit={handleSubmit} className="service-form">
                <Field label="Item Name" required>
                    <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g: Engine Oil 5W-30"
                    />
                </Field>

                <Field label="Part Number">
                    <Input
                        type="text"
                        name="partNumber"
                        value={formData.partNumber}
                        onChange={handleChange}
                        placeholder="e.g: OF-12345"
                    />
                </Field>

                <div className="form-row">
                    <Field label="Unit" required>
                        <Select
                            name="unit"
                            value={formData.unit}
                            onChange={handleChange}
                        >
                            <option value="Liter">Liter</option>
                            <option value="Piece">Piece</option>
                            <option value="Box">Box</option>
                        </Select>
                    </Field>
                    <div>
                        <Field label="Inward Quantity (Total Received)" required>
                            <Input
                                type="number"
                                name="inwardQty"
                                value={inwardQty}
                                onChange={handleInwardQtyChange}
                                min="0"
                                step="any"
                            />
                        </Field>
                        {editData && (
                            <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                                <dl className="ds-kv">
                                    <dt>Historical Consumption</dt>
                                    <dd className="text-red-700">{consumedQty}</dd>
                                    <dt>Current Balance (Available)</dt>
                                    <dd className="text-green-700">{Number(formData.quantity).toLocaleString()}</dd>
                                </dl>
                                <p className="mt-1 text-xs text-gray-500">
                                    To change the available stock, adjust the "Inward Quantity" above.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-row">
                    <Field label="Minimum Stock Level" required>
                        <Input
                            type="number"
                            name="minStock"
                            value={formData.minStock}
                            onChange={handleChange}
                            min="0"
                        />
                    </Field>
                    <Field label="Unit Cost (SAR/AED)" required>
                        <Input
                            type="number"
                            name="unitCost"
                            value={formData.unitCost}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                        />
                    </Field>
                </div>

                <Field label="Vendor Name">
                    <Input
                        type="text"
                        name="vendor"
                        value={formData.vendor}
                        onChange={handleChange}
                        placeholder="e.g: Toyota Al-Futtaim"
                    />
                </Field>

                <div className="pricing-box" role="group" aria-labelledby="inv-pricing-title">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <h4 id="inv-pricing-title" className="text-sm font-semibold text-gray-900">Smart Pricing Calculator</h4>
                        <Checkbox
                            name="useTotalCalculation"
                            checked={formData.useTotalCalculation}
                            onChange={handleChange}
                            label="Calculate from Total"
                        />
                    </div>

                    {formData.useTotalCalculation ? (
                        <div className="form-row">
                            <Field label="Total Invoice (Gross)" required help={`Calculated Unit Price: ${formData.unitCost}`}>
                                <Input
                                    type="number"
                                    name="totalInvoice"
                                    value={formData.totalInvoice}
                                    onChange={handleChange}
                                    placeholder="e.g: 90"
                                    min="0"
                                    step="0.01"
                                />
                            </Field>
                            <div className="ds-field">
                                <span className="ds-field__label">VAT (5%) Adjustment</span>
                                <Checkbox
                                    name="isVATInclusive"
                                    checked={formData.isVATInclusive}
                                    onChange={handleChange}
                                    label="Price includes 5% VAT"
                                />
                            </div>
                        </div>
                    ) : (
                        <Field label="Unit Cost (Net/Single Item)" required>
                            <Input
                                type="number"
                                name="unitCost"
                                value={formData.unitCost}
                                onChange={handleChange}
                                min="0"
                                step="0.01"
                            />
                        </Field>
                    )}
                </div>

                <div className="form-row">
                    <Field label="Invoice Number">
                        <Input
                            type="text"
                            name="invoiceNumber"
                            value={formData.invoiceNumber}
                            onChange={handleChange}
                            placeholder="e.g: INV-2024-001"
                        />
                    </Field>

                    <Field label="Invoice Date">
                        <Input
                            type="date"
                            name="invoiceDate"
                            value={formData.invoiceDate}
                            onChange={handleChange}
                        />
                    </Field>
                </div>
            </form>
        </Dialog>
    );
}
